// TextareaMarkdown.js
// GitHub風：テキストエリアへ貼り付け/ドラッグ&ドロップでアップロード
// Cloud Run の 32MB 制限を避けるため、Active Storage の Direct Upload を使用
//
// 前提：Rails 側は Active Storage が有効で、/api/image.json は { signed_id: ... } を受け取り URL を返す
// 例: { url: "https://.../your-public-url" }
//
// 必要パッケージ（例）:
//   npm i @rails/activestorage whatwg-fetch markdown-it file-type filesize
//
// 使い方（例）:
//   import TextareaMarkdown from "./TextareaMarkdown"
//   const tm = new TextareaMarkdown(document.querySelector("#editor"), {
//     endPoint: "/api/image.json", // signed_id を渡す自前API
//     csrfToken: document.querySelector('meta[name="csrf-token"]')?.content
//   })

import "whatwg-fetch"
import MarkdownIt from "markdown-it"
import { filesize } from "filesize"
import { DirectUpload } from "@rails/activestorage"
const FileType = require("file-type/browser")

export default class TextareaMarkdownDirectUpload {
  constructor(textarea, options = {}) {
    if (!textarea) throw new Error("TextareaMarkdown: textarea is required.")
    this.textarea = textarea

    this.options = Object.assign(
      {
        useUploader: true,
        // Rails側の「signed_id を受け取ってURLを返す」API
        endPoint: "/api/image/direct.json",
        // フロント→Rails で送るときのキー（signed_id）
        signedIdParam: "signed_id",
        // Rails API のレスポンスでURLが入っているキー
        responseKey: "url",

        // UI
        placeholder: "uploading %filename ...",
        // 画像/動画/その他の拡張子（ext判定に利用）
        imageableExtensions: ["jpg", "jpeg", "png", "gif", "webp", "avif"],
        videoExtensions: ["mov", "mp4", "webm"],

        // Markdown プレビュー
        afterPreview: () => {},
        plugins: [],
        markdownOptions: {
          html: true,
          breaks: true,
          langPrefix: "language-",
          linkify: true,
        },

        // 貼り付けるタグ（%filename, %url, %width, %height, %fileSize が利用可）
        uploadImageTag: "![%filename](%url)\n",
        uploadVideoTag: '<video controls src="%url"></video>\n',
        uploadOtherTag: "[%filename (%fileSize)](%url)\n",

        // CSRF
        csrfToken: null,

        // DirectUpload のパス（Rails 既定）
        directUploadPath: "/rails/active_storage/direct_uploads",

        // 失敗時の再試行回数
        retry: 1,

        // 進捗通知（任意）
        onProgress: null, // (percent, file) => {}
      },
      options
    )

    this.previews = []
    this.setPreview()
    this.applyPreview()

    const inputelement = this.setInputElement()

    if (this.options.useUploader) {
      textarea.addEventListener("dragover", (e) => e.preventDefault())
      textarea.addEventListener("drop", (e) => this.drop(e))
    }
    textarea.addEventListener("paste", (e) => this.paste(e))
    textarea.addEventListener("keyup", () => this.keyup())

    if (inputelement) {
      inputelement.addEventListener("click", (e) => (e.target.value = ""))
      inputelement.addEventListener("change", (e) => this.input(e))
    }
  }

  // ====== 初期化/プレビュー ======
  setPreview() {
    const selector = this.textarea.getAttribute("data-preview")
    if (selector) {
      Array.from(document.querySelectorAll(selector), (e) => this.previews.push(e))
    }
  }

  setInputElement() {
    const selector = this.textarea.getAttribute("data-input")
    if (selector) return document.querySelector(selector)
  }

  keyup() {
    this.applyPreview()
  }

  applyPreview() {
    const markdownOptions = this.options.markdownOptions
    const plugins = this.options.plugins
    this.previews.forEach((preview) => {
      let md = new MarkdownIt(markdownOptions)
      plugins.forEach((pluginEntry) => {
        if (Array.isArray(pluginEntry)) {
          const [plugin, ...pluginArgs] = pluginEntry
          md.use(plugin, ...pluginArgs)
        } else {
          md.use(pluginEntry)
        }
      })
      preview.innerHTML = md.render(this.textarea.value)
    })
    this.options.afterPreview()
  }

  // ====== イベント（D&D / Paste / input[file]） ======
  drop(event) {
    event.preventDefault()
    this.uploadAll(event.dataTransfer.files)
  }

  paste(event) {
    const files = event.clipboardData?.files
    if (files && files.length > 0) {
      event.preventDefault()
      this.uploadAll(files)
    }
  }

  input(event) {
    const files = event.target.files
    if (files?.length > 0) this.uploadAll(files)
  }

  // ====== ユーティリティ ======
  triggerEvent(element, event) {
    if (document.createEvent) {
      const evt = document.createEvent("HTMLEvents")
      evt.initEvent(event, true, true)
      return element.dispatchEvent(evt)
    } else {
      const ieEvt = document.createEventObject()
      return element.fireEvent("on" + event, ieEvt)
    }
  }

  insertAtCursor(text) {
    const beforeRange = this.textarea.selectionStart
    const beforeText = this.textarea.value.substring(0, beforeRange)
    const afterText = this.textarea.value.substring(beforeRange)
    this.textarea.value = `${beforeText}${text}${afterText}`
    // キャレットをプレースホルダの後ろに
    const newPos = beforeRange + text.length
    this.textarea.selectionStart = this.textarea.selectionEnd = newPos
  }

  replaceFirstInTextarea(target, replacement) {
    const idx = this.textarea.value.indexOf(target)
    if (idx === -1) return
    this.textarea.value =
      this.textarea.value.substring(0, idx) +
      replacement +
      this.textarea.value.substring(idx + target.length)
  }

  // ====== アップロード（Direct Upload） ======
  async uploadAll(fileList) {
    for (const file of fileList) {
      // eslint-disable-next-line no-await-in-loop
      await this.upload(file)
    }
  }

  async upload(file) {
    // プレースホルダを挿入
    const placeholderText = `![${this.options.placeholder.replace(/filename/g, file.name)}]()\n`
    this.insertAtCursor(placeholderText)
    this.applyPreview()

    // 進捗通知のためのラッパ（ActiveStorage.DirectUpload は進捗イベントを直接出さない）
    const onProgress = typeof this.options.onProgress === "function" ? this.options.onProgress : null
    if (onProgress) onProgress(0, file)

    // DirectUpload で GCS に直送
    const directUploadUrl = this.options.directUploadPath
    const upload = new DirectUpload(file, directUploadUrl)

    const doCreate = () =>
      new Promise((resolve, reject) => {
        upload.create((error, blob) => {
          if (error) reject(error)
          else resolve(blob)
        })
      })

    let blob
    let lastError = null
    const tries = Math.max(1, Number(this.options.retry) + 1)
    for (let i = 0; i < tries; i++) {
      try {
        blob = await doCreate()
        lastError = null
        break
      } catch (err) {
        lastError = err
        if (i === tries - 1) break
      }
    }
    if (lastError) {
      // 失敗：プレースホルダを削除
      this.replaceFirstInTextarea(placeholderText, "")
      this.applyPreview()
      console.error("DirectUpload failed:", lastError)
      return
    }

    if (onProgress) onProgress(60, file) // 体感用

    // signed_id を API に送って、公開URLなどを取得
    const payload = {}
    payload[this.options.signedIdParam] = blob.signed_id

    const headers = { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" }
    if (this.options.csrfToken) headers["X-CSRF-Token"] = this.options.csrfToken

    let json
    try {
      const response = await fetch(this.options.endPoint, {
        method: "POST",
        headers,
        credentials: "same-origin",
        body: JSON.stringify(payload),
      })
      if (!response.ok) throw new Error(`API error: ${response.status}`)
      json = await response.json()
    } catch (e) {
      // 失敗：プレースホルダを削除
      this.replaceFirstInTextarea(placeholderText, "")
      this.applyPreview()
      console.error("Finalize API failed:", e)
      return
    }

    if (onProgress) onProgress(90, file)

    const url = json[this.options.responseKey]
    const fileSizeText = filesize(file.size, { base: 10, standard: "jedec" })

    // 拡張子/種別を判定
    const fileType = await this.safeDetectFileType(file)
    const placeholderTag = this.selectPlaceholderTag(fileType)
    const uploadTag = await this.replacePlaceholderTag(
      placeholderTag,
      file.name,
      fileSizeText,
      url
    )

    // プレースホルダ置換
    this.replaceFirstInTextarea(placeholderText, uploadTag)
    this.applyPreview()

    if (onProgress) onProgress(100, file)
  }

  // ファイルの拡張子/種別を安全に検出（file-type が失敗したらフォールバック）
  async safeDetectFileType(file) {
    try {
      // ArrayBuffer を読む
      const buf = await file.arrayBuffer()
      const bytes = new Uint8Array(buf)
      const ft = await FileType.fromBuffer(bytes)
      if (ft?.ext) return { ext: ft.ext.toLowerCase(), mime: ft.mime }
    } catch (_) {
      // ignore
    }
    // フォールバック：ファイル名と type から推定
    const byName = this.inferFileExt(file.name)
    return { ext: byName, mime: file.type || "" }
  }

  inferFileExt(filename) {
    const m = filename.toLowerCase().match(/\.([0-9a-z]+)$/i)
    return m ? m[1] : ""
  }

  selectPlaceholderTag(fileType) {
    const ext = (fileType?.ext || "").toLowerCase()
    if (this.options.imageableExtensions.includes(ext)) {
      return this.options.uploadImageTag
    } else if (this.options.videoExtensions.includes(ext)) {
      return this.options.uploadVideoTag
    } else {
      return this.options.uploadOtherTag
    }
  }

  async replacePlaceholderTag(placeholderTag, filename, fileSize, url) {
    const base = placeholderTag.replace(/%filename/g, filename).replace(/%url/g, url)

    if (placeholderTag === this.options.uploadImageTag) {
      try {
        const dim = await this.fetchImageDimensions(url)
        return base.replace(/%width/g, dim.width).replace(/%height/g, dim.height)
      } catch {
        // 取得失敗時はそのまま
        return base.replace(/%width/g, "").replace(/%height/g, "")
      }
    } else if (placeholderTag === this.options.uploadVideoTag) {
      return base
    } else {
      return base.replace(/%fileSize/g, fileSize)
    }
  }

  fetchImageDimensions(url) {
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve({ width: image.width, height: image.height })
      image.onerror = (err) => reject(err)
      image.src = url
    })
  }
}
